import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFormContext } from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';

import useKeyDownEvent from '../../hooks/useKeyDownEvent';
import useMouseDownEvent from '../../hooks/useMouseDownEvent';
// import AppliedFilters from '../FilterPanel/AppliedFilters';
import { FilterChipsProvider } from '../FilterPanel/FilterChipsContext';
import FiltersIcon from './FiltersIcon';
import normalizeSearchTerm from './normalizeSearchTerm';
import SearchSuggestions, { MIN_SEARCH_LENGTH } from './SearchSuggestions';
import useDebouncedValue from './useDebouncedValue';
import { SearchDestination, UseSearchSuggestions } from './types';
import * as Style from './style.module.scss';

type Props = {
    name: string;
    searchLabel?: string;
    // The term this field should show for the current page (see SearchController).
    syncedSearch: string;
    pathname: string;
    onSubmit: (params: Record<string, string>) => void;
    // Reports the destination the top suggestion implies, so SearchForm can apply it to whichever
    // submit path fires next.
    onTopSuggestionChange: (showAllTo?: SearchDestination) => void;
    // Resets the search term and all filters (owned by SearchForm).
    onClear: () => void;
    useSearchSuggestions?: UseSearchSuggestions;
    filters?: React.ReactNode;
    filterParams?: string[];
};

const SearchBar: React.FC<Props> = ({
    name,
    searchLabel,
    syncedSearch,
    pathname,
    onSubmit,
    onTopSuggestionChange,
    onClear,
    useSearchSuggestions,
    filters,
    filterParams
}) => {
    const { watch, setValue, getValues, handleSubmit } = useFormContext<Record<string, string>>();
    const search = watch(name) ?? '';
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    // Single open/close state for the shared popover (suggestions + filters).
    const [isOpen, setIsOpen] = React.useState(false);

    const activeFilterCount = React.useMemo(
        () => (filterParams ?? []).filter((key) => !!searchParams.get(key)).length,
        [filterParams, searchParams]
    );

    // Adopt the term the current page expects whenever it changes externally
    // (applying a saved filter, browser back/forward) or we land on another page
    // — the latter clears the field after a jump-to-member. Typing changes
    // neither dependency, so this never clobbers what the user is typing.
    React.useEffect(() => {
        if (getValues(name) !== syncedSearch) {
            setValue(name, syncedSearch);
        }
    }, [syncedSearch, pathname, name, setValue, getValues]);

    const debounced = useDebouncedValue(normalizeSearchTerm(search), 300);
    const effectiveQuery = debounced.length >= MIN_SEARCH_LENGTH ? debounced : '';

    const hasFilters = !!filters;
    const hasSuggestions = !!useSearchSuggestions;
    // The box exists in the DOM whenever filters are present (kept mounted so
    // filter effects — e.g. localStorage restore — run on load), and drops down
    // as a whole once opened. With no query yet the suggestions side shows its
    // "type at least N characters" state rather than staying hidden, so opening
    // always produces something.
    const boxMounted = hasFilters || hasSuggestions;
    const boxVisible = isOpen && boxMounted;

    const closeBox = React.useCallback(() => setIsOpen(false), []);

    const submit = React.useCallback(() => {
        setIsOpen(false);
        handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    // Removes the search term from the URL (only when one is actually set, so we
    // don't needlessly reset other params — and so jump mode, which never sets
    // one, doesn't navigate on every backspace). Goes through onSubmit directly
    // rather than RHF's handleSubmit so it doesn't depend on the just-set
    // value/validation.
    const clearUrlSearch = React.useCallback(() => {
        if (syncedSearch) {
            onSubmit({ [name]: '' });
        }
    }, [syncedSearch, name, onSubmit]);

    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setValue(name, value);
            setIsOpen(true);
            // Emptying the field resets the search in the URL.
            if (!value.trim()) {
                clearUrlSearch();
            }
        },
        [setValue, name, clearUrlSearch]
    );

    // Clear button: reset the search term AND every filter via the URL owner.
    const handleClear = React.useCallback(() => {
        setValue(name, '');
        onClear();
    }, [setValue, name, onClear]);

    const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
        // Escape closes the popover.
        if (e.key === 'Escape') {
            setIsOpen(false);
            return;
        }
        // Pressing "/" opens the search field, unless the user is already typing.
        if (e.key !== '/') {
            return;
        }
        const target = e.target as HTMLElement | null;
        const isTyping =
            !!target &&
            (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable);
        if (isTyping) {
            return;
        }
        const input = wrapperRef.current?.querySelector('input');
        if (input) {
            e.preventDefault();
            input.focus();
            // Opened explicitly rather than leaning on focus: ui-kit's Input spreads
            // its own focus handlers after `...rest`, so an `onFocus` passed to it is
            // silently discarded.
            setIsOpen(true);
        }
    }, []);
    useKeyDownEvent(handleKeyDown);

    // What the field searches, lowercased to read mid-sentence in the placeholder:
    // "Search members" -> "Press / to search members".
    const searchTarget =
        searchLabel && `${searchLabel.charAt(0).toLowerCase()}${searchLabel.slice(1)}`;

    // Click outside the search field closes the popover.
    const handleClickOutside = React.useCallback((e: MouseEvent) => {
        if (e.target instanceof Node && !wrapperRef.current?.contains(e.target)) {
            setIsOpen(false);
        }
    }, []);
    useMouseDownEvent(handleClickOutside);

    return (
        <div style={{ position: 'relative', width: '100%' }} ref={wrapperRef}>
            <Input
                name={name}
                value={search}
                onChange={handleChange}
                // No `onFocus` — ui-kit's Input overrides it with its own (see the
                // "/" handler above). Click and "/" are what open the box.
                onClick={() => setIsOpen(true)}
                // No native placeholder — it can't hold the `/` key cap, so the
                // rendered stand-in below plays that role and this carries the
                // accessible name instead.
                aria-label={searchLabel}
                // Suppress the native browser autocomplete/autofill dropdown so it
                // doesn't overlap our suggestions dropdown.
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                styles={{
                    margin: '0',
                    paddingRight: hasFilters ? '10rem' : '4.8rem'
                }}
            />

            {searchTarget && !search && (
                <span className={Style.placeholder} aria-hidden="true">
                    Press <kbd className={Style.kbd}>/</kbd> to {searchTarget}
                </span>
            )}

            {/* The form's default button, never shown. A form with more than one
                text field and no submit button has no implicit submission, and the
                filters panel below renders its own inputs inside this form — so
                without this, Enter would stop searching on those pages. Enter on a
                highlighted suggestion still wins: SearchSuggestions calls
                preventDefault, which suppresses the implicit submit. */}
            <button type="submit" className={Style.defaultSubmit} tabIndex={-1}>
                Search
            </button>

            <div className={Style.searchControls}>
                {hasFilters && (
                    <button
                        type="button"
                        aria-label="Filters"
                        aria-expanded={isOpen}
                        className={[Style.filtersBadge, activeFilterCount > 0 && Style.active]
                            .filter(Boolean)
                            .join(' ')}
                        onClick={() => setIsOpen((open) => !open)}
                    >
                        <FiltersIcon />
                        {activeFilterCount > 0 && (
                            <span className={Style.badgeCount}>{activeFilterCount}</span>
                        )}
                    </button>
                )}
            </div>

            {boxMounted && (
                <div className={Style.dropdown} style={{ display: boxVisible ? 'flex' : 'none' }}>
                    {hasSuggestions && (
                        <div className={Style.suggestionsArea} role="listbox">
                            <SearchSuggestions
                                effectiveQuery={effectiveQuery}
                                useSearchSuggestions={useSearchSuggestions}
                                isOpen={boxVisible}
                                wrapperRef={wrapperRef}
                                onClose={closeBox}
                                onShowAll={submit}
                                onTopSuggestionChange={onTopSuggestionChange}
                            />
                        </div>
                    )}
                    {hasFilters && (
                        <FilterChipsProvider>
                            <div className={Style.filtersPanel}>
                                <div className={Style.filtersHeader}>
                                    <span className={Style.filtersTitle}>Filters</span>
                                    {activeFilterCount > 0 && (
                                        <button
                                            type="button"
                                            className={Style.clearAll}
                                            onClick={handleClear}
                                        >
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                {/*<AppliedFilters />*/}
                                <div className={Style.filtersGroups}>{filters}</div>
                            </div>
                        </FilterChipsProvider>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
